import moment from "moment";
import React, { useEffect, useState } from "react";
import { useAsyncAxios, utilAxiosWithAppToken, utilAxiosWithAuth } from "../utils/customAxios";

import * as Common from "../commons/common";
import * as Request from "../commons/request";
import * as Strings from "../commons/string";
import * as Types from "../commons/typeDTO";
import * as Utils from "../utils/utils";

import { Button, Card, Checkbox, Col, DatePicker, Divider, List, Modal, Row, Space } from "antd";
import dayjs from "dayjs";
import PageContainer from "../layout/PageContainer";
import ReservationCard from "../components/ReservationCard";

import styles from "./Main.module.css";
import RentScheduler from "../components/RentScheduler";
import { useNavigate } from "react-router-dom";
import SearchDate from "../components/SearchDate";
import ModalChangeRentCar from "../components/ModalChangeRentCar";
import { useAuthState } from "../provider/AuthProvider";
import ModalAdminReservation, { RentAdminReservation } from "../components/ModalAdminReservation";
import { AlertType, useAlert } from "../provider/AlertProvider";

function Main() {
    const userDetails = useAuthState();
    const alert = useAlert();

    const navigate = useNavigate();

    const [searchStartDate, setSearchStartDate] = useState(moment().subtract(1, "M").startOf("month").format(Common.FORMAT_DATE));
    const [searchEndDate, setSearchEndDate] = useState(moment().add(1, "M").endOf("month").format(Common.FORMAT_DATE));

    const [rents, setRents] = useState<Array<Types.RentDto>>([]);
    const [currentRent, setCurrentRent] = useState<Types.RentDto>();
    const [unPaidRent, setUnPaidRent] = useState<Types.RentDto>();
    const [currentUser, setCurrentUser] = useState<Types.UserDto>();
    const [totalRentCarCount, setTotalRentCarCount] = useState(0);
    const [totalActiveRentCarCount, setTotalActiveRentCarCount] = useState(0);
    const [totalRepairingRentCarCount, setTotalRepairingRentCarCount] = useState(0);
    const [totalWaitingRentCarCount, setTotalWaitingRentCarCount] = useState(0);

    const [selectedEvent, setSelectedEvent] = useState<number>();

    const [rentsByStartDate, setRentsByStartDate] = useState<Utils.RentsByDate>();
    const [rentsByEndDate, setRentsByEndDate] = useState<Utils.RentsByDate>();

    const [selectedDate, setSelectedDate] = useState(moment().format(Common.FORMAT_DATE));
    const [startRentsOnDate, setStartRentsOnDate] = useState<Array<Types.RentDto>>([]);
    const [endRentsOnDate, setEndRentsOnDate] = useState<Array<Types.RentDto>>([]);

    const [changableRentCarList, setChangableRentCarList] = useState<Array<Types.RentCarDto>>([]);
    const [openModalChangeRentCar, setOpenModalChangeRentCar] = useState(false);
    const [selectedChangeRent, setSelectedChangeRent] = useState<Types.RentDto>();

    const [openModalAdminReservation, setOpenModalAdminReservation] = useState(false);
    const [showAdminReservation, setShowAdminReservation] = useState(true);

    useEffect(() => {
        Utils.initModalStack();

        requestGetRentCars();
        requestGetRents();

        Utils.onEvent(Common.EVENT_CLOSE_ALL_MODAL, handleCloseAllModal);
        return () => {
            Utils.offEvent(Common.EVENT_CLOSE_ALL_MODAL, handleCloseAllModal);
        };
    }, []);

    const handleCloseAllModal = () => {
        Modal.destroyAll();
        setOpenModalChangeRentCar(false);
    };

    useEffect(() => {
        unPaidRent &&
            Modal.confirm({
                title: "알림",
                content: "미정산금이 있습니다. 정산 완료 처리해 주세요.",
                okText: "확인",
                onOk() {
                    Utils.popModalStack();
                    navigate(Common.PAGE_RENT_DETAIL + "/" + unPaidRent.rentId);
                },
                cancelText: "취소",
                onCancel() {
                    Utils.popModalStack();
                },
                centered: true,
            });
    }, [unPaidRent]);

    const requestAxiosGetRentCars = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_CARS_ALL_URL);
        return response.data;
    };

    const {
        loading: loadingGetRentCars,
        error: errorGetRentCars,
        data: resultGetRentCars,
        execute: requestGetRentCars,
    } = useAsyncAxios(requestAxiosGetRentCars);

    useEffect(() => {
        if (resultGetRentCars === null) return;
        console.log("resultGetRentCars", resultGetRentCars);

        const rentCars = resultGetRentCars.rentCars;
        setTotalRentCarCount(rentCars.length);
        setTotalActiveRentCarCount(rentCars.filter((car: Types.RentCarDto) => car.repairing !== true && car.waiting !== true).length);
        setTotalRepairingRentCarCount(rentCars.filter((car: Types.RentCarDto) => car.repairing === true).length);
        setTotalWaitingRentCarCount(rentCars.filter((car: Types.RentCarDto) => car.waiting === true).length);
    }, [resultGetRentCars]);

    useEffect(() => {
        if (errorGetRentCars === null) return;
        console.log("errorGetRentCars", errorGetRentCars);
    }, [errorGetRentCars]);

    const requestAxiosGetRents = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_ALL_URL, {
            params: {
                startDate: searchStartDate,
                endDate: searchEndDate,
            },
        });
        return response.data;
    };

    const { loading: loadingGetRents, error: errorGetRents, data: resultGetRents, execute: requestGetRents } = useAsyncAxios(requestAxiosGetRents);

    useEffect(() => {
        if (resultGetRents === null) return;
        console.log("resultGetRents", resultGetRents);
        setRents(resultGetRents.rents);

        const unPaidRentArray: Array<Types.RentDto> = [];
        resultGetRents.rents.map((rent: Types.RentDto) => {
            if (rent.rentStatus !== "CANCELED" && moment(rent.endDate).isBefore(moment()) && rent.rentPayments.find((payment) => payment.paidDate === null)) {
                unPaidRentArray.push(rent);
            }
        });
        setUnPaidRent(unPaidRentArray[0]);

        setRentsByStartDate(Utils.convertRentByStartDate(resultGetRents.rents, showAdminReservation));
        setRentsByEndDate(Utils.convertRentByEndDate(resultGetRents.rents, showAdminReservation));

        if (currentRent !== undefined) {
            const currentRents = resultGetRents.rents.filter((rent: Types.RentDto) => rent.rentId === currentRent.rentId);
            if (currentRents.length > 0) setCurrentRent(currentRents[0]);
        }

        if (selectedDate !== undefined) {
            setStartRentsOnDate(
                resultGetRents.rents.filter(
                    (rent: Types.RentDto) =>
                        rent.startDate === selectedDate &&
                        rent.rentStatus !== "CANCELED" &&
                        (showAdminReservation || rent.user.email !== "support@alpamotors.com")
                )
            );
            setEndRentsOnDate(
                resultGetRents.rents.filter(
                    (rent: Types.RentDto) =>
                        rent.endDate === selectedDate &&
                        rent.rentStatus !== "CANCELED" &&
                        (showAdminReservation || rent.user.email !== "support@alpamotors.com")
                )
            );
        }
    }, [resultGetRents]);

    useEffect(() => {
        if (errorGetRents === null) return;
        console.log("errorGetRents", errorGetRents);
    }, [errorGetRents]);

    useEffect(() => {
        setStartRentsOnDate(
            rents.filter(
                (rent) =>
                    rent.startDate === selectedDate && rent.rentStatus !== "CANCELED" && (showAdminReservation || rent.user.email !== "support@alpamotors.com")
            )
        );
        setEndRentsOnDate(
            rents.filter(
                (rent) =>
                    rent.endDate === selectedDate && rent.rentStatus !== "CANCELED" && (showAdminReservation || rent.user.email !== "support@alpamotors.com")
            )
        );
    }, [selectedDate]);

    useEffect(() => {
        setRentsByStartDate(Utils.convertRentByStartDate(rents, showAdminReservation));
        setRentsByEndDate(Utils.convertRentByEndDate(rents, showAdminReservation));

        setStartRentsOnDate(rents.filter((rent) => rent.startDate === selectedDate && (showAdminReservation || rent.user.email !== "support@alpamotors.com")));
        setEndRentsOnDate(rents.filter((rent) => rent.endDate === selectedDate && (showAdminReservation || rent.user.email !== "support@alpamotors.com")));
    }, [showAdminReservation]);

    const onClickSchedulerDate = (date: string) => {
        setSelectedDate(moment(date).format(Common.FORMAT_DATE));
    };

    const onChangedSearchDate = (type: string, value: string) => {
        if (type === "startDate") setSearchStartDate(moment(value).format(Common.FORMAT_DATE));
        else if (type === "endDate") setSearchEndDate(moment(value).format(Common.FORMAT_DATE));
    };

    const onSearhRents = () => {
        requestGetRents();
    };

    const changeRentCar = (rent: Types.RentDto) => {
        setOpenModalChangeRentCar(true);
        setSelectedChangeRent(rent);
    };

    useEffect(() => {
        selectedChangeRent && requestGetChangeRentCars(selectedChangeRent);
    }, [selectedChangeRent]);

    const requestAxiosGetChangeRentCars = async (rent: Types.RentDto) => {
        const response = await utilAxiosWithAuth().get(Request.RENT_CARS_ALL_URL, {
            params: {
                rentRegionId: rent.rentCar.rentRegion.rentRegionId,
                // startDate: rent.startDate,
                // endDate: rent.endDate,
            },
        });
        return response.data;
    };

    const {
        loading: loadingGetChangeRentCars,
        error: errorGetChangeRentCars,
        data: resultGetChangeRentCars,
        execute: requestGetChangeRentCars,
    } = useAsyncAxios(requestAxiosGetChangeRentCars);

    useEffect(() => {
        if (resultGetChangeRentCars === null) return;
        console.log("resultGetChangeRentCars", resultGetChangeRentCars);
        // setChangableRentCarList(
        //     resultGetChangeRentCars.rentCars.filter(
        //         (rentCar: Types.RentCarDto) => rentCar.car.carNumber !== selectedChangeRent?.rentCar.car.carNumber
        //     )
        // );
        setChangableRentCarList(resultGetChangeRentCars.rentCars);
    }, [resultGetChangeRentCars]);

    useEffect(() => {
        if (errorGetChangeRentCars === null) return;
        console.log("errorGetChangeRentCars", errorGetChangeRentCars);
    }, [errorGetChangeRentCars]);

    const onChangeRentCar = (rent: Types.RentDto, rentCarId: number) => {
        requestChangeRentCar(rent.rentId, rentCarId);
    };

    const requestAxiosChangeRentCar = async (rentId: number, rentCarId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, { usedRentCar: { rentCarId: rentCarId } });
        return response.data;
    };

    const {
        loading: loadingChangeRentCar,
        error: errorChangeRentCar,
        data: resultChangeRentCar,
        execute: requestChangeRentCar,
    } = useAsyncAxios(requestAxiosChangeRentCar);

    useEffect(() => {
        if (resultChangeRentCar === null) return;
        console.log("resultChangeRentCar", resultChangeRentCar);
        requestGetRents();
    }, [resultChangeRentCar]);

    useEffect(() => {
        if (errorChangeRentCar === null) return;
        console.log("errorChangeRentCar", errorChangeRentCar);
    }, [errorChangeRentCar]);

    const onAdminReservation = () => {
        setOpenModalAdminReservation(true);
    };

    const onChangeRentAdminReservation = (rentInfo: RentAdminReservation) => {
        requestAdminReservation(rentInfo);
    };

    const requestAxiosAdminReservation = async (rentInfo: RentAdminReservation) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_ADD_ADMIN_URL, null, { params: rentInfo });
        return response.data;
    };

    const {
        loading: loadingAdminReservation,
        error: errorAdminReservation,
        data: resultAdminReservation,
        execute: requestAdminReservation,
    } = useAsyncAxios(requestAxiosAdminReservation);

    useEffect(() => {
        if (resultAdminReservation === null) return;
        console.log("resultAdminReservation", resultAdminReservation);

        alert.setAlert(AlertType.SUCCESS, "", "예약이 완료되었습니다.");

        requestGetRents();
    }, [resultAdminReservation]);

    useEffect(() => {
        if (errorAdminReservation === null) return;
        console.log("errorAdminReservation", errorAdminReservation);
    }, [errorAdminReservation]);

    return (
        <PageContainer>
            <Space direction="vertical" size={24} style={{ width: "100%" }}>
                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                    {/* <Divider className={styles.subTitle} orientation="left">
                        <span>지역</span>
                    </Divider>
                    <Row gutter={10} style={{ width: "100%", margin: 0 }}>
                        <Col xs={12} className="gutter-row">
                            <Button className="b-xs" style={{ width: "100%" }}>
                                경주
                            </Button>
                        </Col>
                        <Col xs={12} className="gutter-row">
                            <Button disabled className="b-xs" style={{ width: "100%" }}>
                                제주
                            </Button>
                        </Col>
                    </Row> */}
                    {/* <Divider className={styles.subTitle} orientation="left">
                        <span>차량 현황</span>
                    </Divider>
                    <div className={styles.box}>
                        <Row align="middle" justify="space-between">
                            <Col>{Strings.totalRentCarCount}</Col>
                            <Col>
                                <span className={styles.highlightText}>{totalRentCarCount.toString()}</span>
                                <span className={styles.lgText}>{Strings.count_car}</span>
                            </Col>
                        </Row>
                        <Row align="middle" justify="space-between">
                            <Col>{Strings.totalActiveRentCarCount}</Col>
                            <Col>
                                <span className={styles.highlightText}>{totalActiveRentCarCount.toString()}</span>
                                <span className={styles.lgText}>{Strings.count_car}</span>
                            </Col>
                        </Row>
                        <Row align="middle" justify="space-between">
                            <Col>{Strings.totalRepairingRentCarCount}</Col>
                            <Col>
                                <span className={styles.highlightText}>{totalRepairingRentCarCount.toString()}</span>
                                <span className={styles.lgText}>{Strings.count_car}</span>
                            </Col>
                        </Row>
                        <Row align="middle" justify="space-between">
                            <Col>{Strings.totalWaitingRentCarCount}</Col>
                            <Col>
                                <span className={styles.highlightText}>{totalWaitingRentCarCount.toString()}</span>
                                <span className={styles.lgText}>{Strings.count_car}</span>
                            </Col>
                        </Row>
                    </div> */}
                </Space>
                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                    <Row align={"middle"} justify={"space-between"}>
                        <Checkbox checked={showAdminReservation} onChange={(e) => setShowAdminReservation(e.target.checked)}>
                            관리자 예약 보기
                        </Checkbox>
                        {userDetails?.user?.authority === "ROLE_ADMIN" && <Button onClick={onAdminReservation}>관리자 예약</Button>}
                    </Row>
                    <Space size={10}>
                        <SearchDate startDate={searchStartDate} endDate={searchEndDate} onSearch={onChangedSearchDate} />
                        <Button onClick={onSearhRents}>검색</Button>
                    </Space>
                    <Row className={styles.calendar}>
                        <Col span={24} className={styles.box}>
                            <RentScheduler startRents={rentsByStartDate} endRents={rentsByEndDate} onClickDate={onClickSchedulerDate} />
                        </Col>
                        <Col span={24}>
                            <h3 style={{ marginBottom: 0 }}>{moment(selectedDate).format(Common.FORMAT_DATE_KOREAN)}</h3>
                            <List
                                className={styles.list}
                                header={
                                    <>
                                        <h4 style={{ margin: 0 }}>
                                            {Strings.rent_return} : {endRentsOnDate.length}건
                                        </h4>
                                        <Divider className={styles.divider} />
                                    </>
                                }
                                dataSource={endRentsOnDate}
                                split={false}
                                renderItem={(item) => {
                                    const color = Common.COLOR_RETURN;
                                    const duringPaymentColor = item.rentStatus === "DURING_PAYMENT" ? "red" : "black";
                                    return (
                                        <List.Item>
                                            <Row>
                                                <Space size={8} className={styles.info}>
                                                    <div
                                                        style={{
                                                            width: 12,
                                                            height: 12,
                                                            backgroundColor: color,
                                                            margin: 0,
                                                            borderRadius: "50%",
                                                        }}
                                                    />
                                                    <Row align="middle" justify="space-between" style={{ width: "100%" }}>
                                                        <p className={styles.subTitle}>
                                                            {item?.rentCar?.car?.carType?.typeName}_{" "}
                                                            {item?.rentCar?.title?.includes("★") ? "[특가]" : item.rentCar.b2b ? "[B2B]" : ""}
                                                            {item?.rentCar?.car?.carNumber}
                                                            {item.usedRentCar && "(" + item.usedRentCar.car.carNumber + ")"}
                                                        </p>
                                                        <p className={styles.subTitle} style={{ color: duringPaymentColor }}>
                                                            {item.user.name}_{item.user.phone.substring(item.user.phone.length - 4, item.user.phone.length)}
                                                        </p>
                                                    </Row>
                                                </Space>
                                                <Row gutter={10} style={{ width: "100%", margin: 0 }}>
                                                    <Col xs={12} className="gutter-row">
                                                        <Button style={{ width: "100%" }} onClick={() => navigate(Common.PAGE_RENT_DETAIL + "/" + item.rentId)}>
                                                            예약 상세보기
                                                        </Button>
                                                    </Col>
                                                    <Col xs={12} className="gutter-row">
                                                        <Button style={{ width: "100%" }} onClick={() => changeRentCar(item)} disabled={item.usedDate !== null}>
                                                            차량 변경
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </Row>
                                        </List.Item>
                                    );
                                }}
                            />
                            <List
                                className={styles.list}
                                header={
                                    <>
                                        <h4 style={{ margin: 0 }}>
                                            {Strings.rent_use} : {startRentsOnDate.length}건
                                        </h4>
                                        <Divider className={styles.divider} />
                                    </>
                                }
                                dataSource={startRentsOnDate}
                                split={false}
                                renderItem={(item) => {
                                    const color = Common.COLOR_USE;
                                    const duringPaymentColor = item.rentStatus === "DURING_PAYMENT" ? "red" : "black";
                                    return (
                                        <List.Item>
                                            <Row>
                                                <Space size={8} className={styles.info}>
                                                    <div
                                                        style={{
                                                            width: 12,
                                                            height: 12,
                                                            backgroundColor: color,
                                                            margin: 0,
                                                            borderRadius: "50%",
                                                        }}
                                                    />
                                                    <Row align="middle" justify="space-between" style={{ width: "100%" }}>
                                                        <p className={styles.subTitle}>
                                                            {item?.rentCar?.car?.carType?.typeName}_{" "}
                                                            {item?.rentCar?.title?.includes("★") ? "[특가]" : item.rentCar.b2b ? "[B2B]" : ""}
                                                            {item.rentCar.car.carNumber}
                                                            {item.usedRentCar && "(" + item.usedRentCar.car.carNumber + ")"}
                                                        </p>
                                                        <p className={styles.subTitle} style={{ color: duringPaymentColor }}>
                                                            {item.user.name}_{item.user.phone.substring(item.user.phone.length - 4, item.user.phone.length)}
                                                        </p>
                                                    </Row>
                                                </Space>
                                                <Row gutter={10} style={{ width: "100%", margin: 0 }}>
                                                    <Col xs={12} className="gutter-row">
                                                        <Button style={{ width: "100%" }} onClick={() => navigate(Common.PAGE_RENT_DETAIL + "/" + item.rentId)}>
                                                            예약 상세보기
                                                        </Button>
                                                    </Col>
                                                    <Col xs={12} className="gutter-row">
                                                        <Button style={{ width: "100%" }} onClick={() => changeRentCar(item)} disabled={item.usedDate !== null}>
                                                            차량 변경
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </Row>
                                        </List.Item>
                                    );
                                }}
                            />
                        </Col>
                    </Row>
                </Space>
            </Space>

            <ModalChangeRentCar
                rentCars={changableRentCarList}
                open={openModalChangeRentCar}
                onChangedOpen={(open: boolean) => {
                    setOpenModalChangeRentCar(open);
                }}
                onChangedRentCar={(rentCarId) => {
                    selectedChangeRent && onChangeRentCar(selectedChangeRent, rentCarId);
                    setOpenModalChangeRentCar(false);
                }}
            />

            {openModalAdminReservation && (
                <ModalAdminReservation
                    open={openModalAdminReservation}
                    onChangedOpen={(open: boolean) => setOpenModalAdminReservation(open)}
                    onChangeRentInfo={onChangeRentAdminReservation}
                />
            )}
        </PageContainer>
    );
}

export default Main;
